import type { Hex } from "viem";
import type { ChainWriter, ExtensionEntry } from "@woodpecker/chain";
import { type ExtensionManifest, type InstallRecord } from "@woodpecker/protocol";
import { fetchManifest } from "./extensions";

/** An installed extension as the Console shows it: the install record, what the chain says now, and the pinned check. */
export interface InstalledView {
  record: InstallRecord;
  /** The manifest at the pinned url, when it hashes to the pinned value (D79); null otherwise. */
  manifest: ExtensionManifest | null;
  /** The manifest at the pinned url does not hash to the pinned value, or does not answer. */
  problem: string | null;
  /** The directory's entry today: a newer version shows as an update. */
  entry: ExtensionEntry | null;
  newerHash: Hex | null;
  unpublished: boolean;
}

/**
 * The installed extensions (D72, D79): for each install record, the manifest fetched from the pinned url and
 * checked against the pinned hash, and the directory's entry today (a newer hash on chain is an update the person
 * may install again; an inactive entry is unpublished, the pinned copy keeps working).
 */
export async function loadInstalled(chain: ChainWriter, records: InstallRecord[]): Promise<InstalledView[]> {
  return Promise.all(
    records.map(async (record): Promise<InstalledView> => {
      const m = await fetchManifest(record.url);
      const ok = !!m.manifest && !!m.hash && m.hash.toLowerCase() === record.hash.toLowerCase();
      let entry: ExtensionEntry | null = null;
      try {
        entry = await chain.extEntry(record.owner, record.id.split("/")[1] ?? "");
      } catch {
        entry = null;
      }
      return {
        record,
        manifest: ok ? m.manifest : null,
        problem: ok ? null : (m.problem ?? "the manifest at the pinned url does not hash to the pinned value"),
        entry,
        newerHash: entry && entry.hash.toLowerCase() !== record.hash.toLowerCase() ? entry.hash : null,
        unpublished: !!entry && !entry.active,
      };
    }),
  );
}

/** The one view of an installed extension, for its tab. */
export async function loadInstalledOne(chain: ChainWriter, record: InstallRecord): Promise<InstalledView> {
  return (await loadInstalled(chain, [record]))[0]!;
}
